<template>
	<div class="preview-player">
		<Player
			v-if="hlsStreamAvailable && hlsUrl"
			v-show="isInitialized"
			ref="player"
			class="preview-player__player"
			:class="{ '-playing': isPlaying }"
			:hls-url="hlsUrl"
			:show-controls="false"
			:pointer-events="false"
			:show-errors="false"
			:loading-spinner-enabled="false"
			:hls-config="{ maxBufferSize: 4000000 }"
			@quality-levels-updated="onQualityLevelsUpdated"
			@is-playing-change="onIsPlayingChange"
			@timeupdate="handleTimeUpdate"
			@loadedmetadata="handleLoadedMetadata"
			@error="handlePlaybackError"
		/>
	</div>
</template>

<script>
import Player from '@shared/components/Player.vue';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'PreviewPlayer',
	components: { Player },
	props:      {
		eventId: {
			type:     Number,
			required: true,
		},
		playPreview: {
			type:    Boolean,
			default: false,
		},
		videoQuality: {
			type:    String,
			default: '360p',
		},
	},
	data() {
		return {
			isPlaying:          false,
			isInitialized:      false,
			hlsStreamAvailable: false,
			hlsLoaded:          false,
			hlsUrl:             '',
			playerQualityLevel: null,
			elapsed:            0,
			videoDuration:      0,
		};
	},
	watch: {
		playPreview(newValue) {
			if (newValue) {
				if (!this.isInitialized) {
					this.initPlayer();
				} else {
					this.play();
				}
			} else {
				this.stopPlayer();
			}
		},
	},
	methods: {
		onIsPlayingChange(isPlaying) {
			this.isPlaying = isPlaying;
			if (isPlaying) {
				this.isInitialized = true;
			}
			this.$emit('is-playing-change', isPlaying);
		},
		async initPlayer() {
			await this.loadHlsUrl();

			if (this.$refs.player) {
				this.$refs.player.mute();
				if (this.playPreview) {
					this.$refs.player.play();
				}
			}
		},
		async loadHlsUrl() {
			if (this.hlsLoaded) {
				return;
			}
			try {
				const response = await httpClient.getEventPlayout(this.eventId);
				const data = response.data;
				const hlsUrl = data?.hlsUrl;

				if (hlsUrl && !data.isPreview) { // We don't show the stream preview in the preview player
					this.hlsUrl = hlsUrl;
					this.hlsStreamAvailable = true;
					this.$emit('hls-stream-available', true);
				} else {
					this.hlsStreamAvailable = false;
				}
			} catch (error) {
				this.hlsStreamAvailable = false;
			} finally {
				this.hlsLoaded = true;
			}
		},
		onQualityLevelsUpdated(levels) {
			const index360p = levels.findIndex((level) => level.height === 360);
			const index720p = levels.findIndex((level) => level.height === 720);
			if (this.videoQuality === '720p') {
				if (index720p !== -1 && index720p !== this.playerQualityLevel) {
					this.$refs.player.setQuality(index720p);
					this.playerQualityLevel = index720p;
				}
			} else {
				if (index360p !== -1 && index360p !== this.playerQualityLevel) {
					this.$refs.player.setQuality(index360p);
					this.playerQualityLevel = index360p;
				}
			}
		},
		handleTimeUpdate(time) {
			this.elapsed = time;
			this.$emit('timeupdate', time);
		},
		handleLoadedMetadata() {
			this.videoDuration = this.$refs.player.getDuration();
			this.$emit('loadedmetadata', this.videoDuration);
		},
		handlePlaybackError(error) {
			if (!error?.fatal) {
				return;
			}

			// A failed video surface can otherwise cover the card after its poster was hidden.
			this.hlsStreamAvailable = false;
			this.isPlaying = false;
			this.$emit('hls-stream-available', false);
		},
		stopPlayer() {
			if (this.$refs.player) {
				this.$refs.player.pause();
			}
		},
		setVideoTime(time) {
			this.elapsed = time;
			if (this.$refs.player) {
				this.$refs.player.seek(time);
			}
		},
		// Public methods for external control
		play() {
			if (this.$refs.player) {
				this.$refs.player.play();
			}
		},
		stop() {
			this.stopPlayer();
		},
		seek(time) {
			this.setVideoTime(time);
		},
		// Getters for external access
		getElapsed() {
			return this.elapsed;
		},
		getDuration() {
			return this.videoDuration;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.preview-player {
	position: relative;
	width: 100%;
	height: 100%;
}

.preview-player__player {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}
</style>
